import { formatDate, formatTime } from '@/shared/formatters';

import type {
  CobrancaProcessoCliente,
  CobrancaProcessoClienteApi,
  StatusCobrancaCliente,
} from '../types/financeiroProcessoClienteTypes';

/**
 * Adiciona o rótulo de rito à cobrança de custas. É o único campo que a API não
 * manda (o rito não viaja por cobrança — o front já tem `processo.tipoRito`), e
 * separar essa etapa evita que a leitura do financeiro dependa do processo já ter
 * carregado.
 *
 * Tudo o mais vem pronto do servidor, inclusive os três números do resumo e as
 * `acoes` de cada linha. O front **não soma e não deriva ação**: as duas réguas
 * que existiam aqui (`resumoDasCobrancas` e o contador de cobranças) divergiam do
 * backend em `ERRO_EMISSAO` — a tela mostrava R$ 0,00 com cobrança listada — e
 * somavam apenas a página recebida.
 */
export function montarCobrancasCliente(
  api: CobrancaProcessoClienteApi[],
  tipoRito: string,
): CobrancaProcessoCliente[] {
  return api.map((cobranca) => ({
    ...cobranca,
    detalhe: cobranca.finalidade === 'CUSTAS_PROCESSO' ? `Rito ${tipoRito.toLowerCase()}` : null,
  }));
}

/**
 * Fim do prazo adicional da custa (C36), como card, banner e aba Financeiro o escrevem:
 * `09/11/2026, às 23h59 (horário de Brasília)`.
 *
 * Data e hora saem do INSTANTE que o servidor manda, no fuso de Brasília — nunca de um
 * "23h59" cravado no texto, que divergiria do corte do core se ele mudar. `null` quando
 * não há instante legível: quem chama omite o "até …" em vez de escrever "até , às 23h59".
 */
export function formatarPrazoFinalPagamento(prazo: string | null): string | null {
  const dia = formatDate(prazo);
  if (!dia) return null;
  return `${dia}, às ${formatTime(prazo).replace(':', 'h')} (horário de Brasília)`;
}

export function getStatusCobrancaLabel(status: StatusCobrancaCliente): string {
  const labels: Record<StatusCobrancaCliente, string> = {
    EM_ABERTO: 'Em aberto',
    // Envio de AR que venceu no relógio, sem o gateway ter marcado.
    EM_ATRASO: 'Em atraso',
    // Custa vencida, ainda pagável pelo mesmo link (C36). Texto a validar com a Bianca.
    EM_PRORROGACAO: 'Prazo adicional',
    PAGA: 'Paga',
    VENCIDA: 'Vencida',
    CANCELADA: 'Cancelada',
    ESTORNADA: 'Estornada',
    ERRO_EMISSAO: 'Erro na emissão',
  };
  return labels[status];
}

export function getStatusCobrancaVariant(
  status: StatusCobrancaCliente,
): 'brand' | 'neutral' | 'error' | 'warning' | 'success' {
  const variants: Record<
    StatusCobrancaCliente,
    'brand' | 'neutral' | 'error' | 'warning' | 'success'
  > = {
    EM_ABERTO: 'warning',
    EM_ATRASO: 'warning',
    EM_PRORROGACAO: 'error',
    PAGA: 'success',
    VENCIDA: 'error',
    CANCELADA: 'neutral',
    ESTORNADA: 'warning',
    ERRO_EMISSAO: 'error',
  };
  return variants[status];
}
